// Google Ads conversion measurement for portal signups (Layer 1 of the
// click -> trial -> paying-customer chain).
//
// Sibling of ultimo-bots-webpage/lib/adsTracking.js: the marketing site's tag
// sets Google's first-party _gcl_aw cookie on .ultimo-bots.com from ?gclid=;
// this module runs the SAME Google tag on the portal so that when the signup
// conversion fires here, gtag finds that cookie and attributes the trial to
// the ad click. Keep the consent defaults in sync with the webpage module.
//
// Everything is gated on REACT_APP_AW_ID / REACT_APP_AW_CONVERSION_LABEL:
// unset (dev, preview builds) = no script loads, no event fires.
//
// fireTrialConversion() is called from exactly two places, both real NEW
// registrations (never logins):
//   - pages/Register.js on a successful email/password signup
//   - App.js on the ?token=&just_registered=1 return leg of a Google OAuth
//     signup (the backend appends just_registered only when it created a user)
// Deliberately NOT wired into the Wix (?instance= / /wix-start), WordPress
// (/wordpress-register, ?connect= or legacy params), Webflow, or /create-chatbot landing
// flows — those signups are not ad-attributable website signups and would
// pollute the conversion signal Google bids on.

const AW_ID = (process.env.REACT_APP_AW_ID || '').replace(/[^A-Za-z0-9_-]/g, '');
const AW_CONVERSION_LABEL = (process.env.REACT_APP_AW_CONVERSION_LABEL || '').replace(
  /[^A-Za-z0-9_-]/g,
  ''
);

// EEA + UK + CH: ad-signal defaults denied until a future consent banner
// calls grantAdConsent() (Consent Mode v2 region scoping). Must stay
// identical to CONSENT_DENIED_REGIONS in the webpage module.
const CONSENT_DENIED_REGIONS = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
  'HU', 'IS', 'IE', 'IT', 'LV', 'LI', 'LT', 'LU', 'MT', 'NL', 'NO', 'PL',
  'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'GB', 'CH',
];

// localStorage latch so a refresh / back-navigation of the post-signup URL
// can never double-fire the conversion for the same browser.
const FIRED_LATCH_KEY = 'ub_aw_trial_conversion_fired';

// Load gtag.js and queue the Consent Mode v2 defaults + config. Called once
// from src/index.js at boot; a no-op when REACT_APP_AW_ID is unset.
export function initGoogleTag() {
  if (!AW_ID || typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window._ubGtagLoaded) return;
  window._ubGtagLoaded = true;

  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = window.gtag || gtag;

  // Defaults are queued before config, so they apply no matter when the
  // external script finishes loading.
  window.gtag('consent', 'default', {
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
    analytics_storage: 'denied',
  });
  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    region: CONSENT_DENIED_REGIONS,
  });
  window.gtag('set', 'url_passthrough', true);
  window.gtag('js', new Date());
  window.gtag('config', AW_ID);

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${AW_ID}`;
  document.head.appendChild(script);
}

// The single wiring point for the future consent banner: call on "accept".
// Nothing calls it today (no banner yet — known, deliberately deferred).
export function grantAdConsent() {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
    analytics_storage: 'granted',
  });
}

// Fire the "Trial gestartet" signup conversion exactly once per browser.
// Safe to call unconditionally from a successful-registration code path:
// gated on the env pair, latched in localStorage, and swallows everything —
// a measurement failure must never disturb the signup UX.
export function fireTrialConversion() {
  try {
    if (!AW_ID || !AW_CONVERSION_LABEL) return;
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    if (localStorage.getItem(FIRED_LATCH_KEY)) return;
    localStorage.setItem(FIRED_LATCH_KEY, new Date().toISOString());
    window.gtag('event', 'conversion', {
      send_to: `${AW_ID}/${AW_CONVERSION_LABEL}`,
    });
  } catch (e) {
    // Never let measurement break registration.
  }
}
